// SERP snippet polishing. Runs on every page's <meta name="description">
// and og/twitter description via pageHeadForProvince().
//
// Goals:
//  1) Maximise click-through from Google results (hook + proof + clear CTA).
//  2) NEVER expose a price in the snippet — pricing is quoted case-by-case,
//     and a number in the SERP loses deals before we can talk to the client.
//  3) Stay inside Google's ~155-160 char display window without cutting a word.

const MAX_LEN = 158;

/** Price-ish fragments that must never reach a SERP snippet. */
const PRICE_PATTERNS: RegExp[] = [
  // currency symbol amounts, incl. ranges
  /(?:เริ่มต้น(?:เพียง)?\s*)?[฿$€£]\s?[\d,]+(?:\.\d+)?(?:\s*[-–—]\s*[฿$€£]?\s?[\d,]+(?:\.\d+)?)?(?:\s*\/\s*[^\s·,]+)?/g,
  // amount (or range) followed by a currency word, incl. "/หน้า", "per page"
  /(?:ราคา|เริ่มต้น(?:เพียง)?\s*)?[\d,]+(?:\.\d+)?(?:\s*[-–—]\s*[\d,]+(?:\.\d+)?)?\s*(?:บาท|baht|THB|USD)(?![\wก-๙])(?:\s*\/\s*[^\s·,]+|\s*(?:per|ต่อ)\s*[^\s·,]+)?/gi,
  // orphan currency word left after a number was removed upstream
  /(?:^|(?<=[\s·—–-]))(?:บาท|baht|THB)(?![\wก-๙])(?:\s*\/\s*[^\s·,]+)?/gi,
  // bare thousand-separated amounts and ranges (e.g. "2,500–5,000")
  /(?:ราคา|เริ่มต้น(?:เพียง)?\s*)?\b\d{1,3},\d{3}(?:\s*[-–—]\s*\d{1,3},?\d{3})?\b/g,
  /\bfrom\s+[฿$]?\s?[\d,]+/gi,
  /ราคา(?:เริ่มต้น|เพียง|พิเศษ)?\s*(?=[·,—–-]|$)/g,
];

const TH_CTAS = [
  "ทักไลน์เช็กเอกสารฟรี ตอบใน 30 นาที",
  "ส่งรูปเอกสารมาประเมินฟรี รู้ผลใน 30 นาที",
  "ปรึกษาทนายตัวจริงฟรี ก่อนตัดสินใจ",
  "นัดคิววันนี้ รับเอกสารได้ไว",
  "โทร/ไลน์หาเราได้เลย ทีมงานพร้อมดูแลถึงที่",
  "ทักไลน์ตอนนี้ ทีมทนายตอบเองทุกเคส",
  "เช็กว่าเอกสารคุณผ่านไหม ฟรี ไม่มีข้อผูกมัด",
  "ส่งเอกสารก่อน 15.00 น. เริ่มงานวันนี้เลย",
  "ทักมาเลย ทีมงานสรุปขั้นตอนให้ใน 1 ข้อความ",
];

const EN_CTAS = [
  "Chat with a Thai lawyer free — reply within 30 minutes.",
  "Send us your documents for a free eligibility check.",
  "Book today — same-day appointments available.",
  "Talk to a licensed notarial services attorney now.",
  "Message us on LINE — a lawyer replies, not a bot.",
  "Free document review before you pay anything.",
  "Send scans before 3pm and we start the same day.",
];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

export function stripPrices(input: string): string {
  let out = input;
  for (const re of PRICE_PATTERNS) out = out.replace(re, "");
  return out
    .replace(/\(\s*\)/g, "")
    .replace(/\s*·\s*(?=·)/g, "")
    .replace(/\s*[-–—]\s*(?=·|$)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.·])/g, "$1")
    .replace(/[·,\-–—/]\s*$/g, "")
    .trim();
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const i = Math.max(cut.lastIndexOf(" "), cut.lastIndexOf("·"), cut.lastIndexOf(","));
  return (i > max * 0.6 ? cut.slice(0, i) : cut).replace(/[\s·,\-–—]+$/, "") + "…";
}

const HAS_CTA_TH =
  /(ทักไลน์|ไลน์|LINE|โทร|ปรึกษา|นัดคิว|ติดต่อ|สอบถาม|ประเมินฟรี)/i;
const HAS_CTA_EN = /(chat|contact|call|book|talk to|get a|free check|reply)/i;

/**
 * Clean + CTA-optimise a meta description.
 * Pure string work — no pricing, no claims beyond what the page states.
 */
export function polishSnippet(
  description: string,
  opts: { locale?: "th_TH" | "en_US"; seed?: string } = {},
): string {
  const en = opts.locale === "en_US";
  const base = stripPrices(description ?? "").replace(/\s+/g, " ").trim();
  if (!base) return base;

  const alreadyHasCta = (en ? HAS_CTA_EN : HAS_CTA_TH).test(base);
  if (alreadyHasCta) return truncate(base, MAX_LEN);

  const pool = en ? EN_CTAS : TH_CTAS;
  const cta = pool[hash(opts.seed ?? base) % pool.length];
  const sep = " — ";
  const core = base.replace(/[.\s·]+$/, "");
  const combined = `${core}${sep}${cta}`;
  if (combined.length <= MAX_LEN) return combined;

  // Not enough room for the full CTA: shorten the body, keep the CTA intact.
  const room = MAX_LEN - cta.length - sep.length;
  if (room < 40) return truncate(core, MAX_LEN);
  return `${truncate(core, room).replace(/…$/, "")}${sep}${cta}`;
}

// ---------------------------------------------------------------------------
// SERP title clamp. Google truncates titles past ~60 chars (~600px). Titles are
// authored long for keyword coverage; this trims them at a natural boundary so
// the head tag stays inside the display window without cutting a word.
// ---------------------------------------------------------------------------

const MAX_TITLE = 60;
/** Separators used across the site's title templates, most-preferred first. */
const TITLE_SEPARATORS = [" — ", " | ", " · ", " – ", " - ", ", "];

export function clampTitle(input: string, max: number = MAX_TITLE): string {
  const title = input.replace(/\s+/g, " ").trim();
  if (title.length <= max) return title;
  // Never clamp down to a stub: a clamped title must keep most of the window.
  const min = Math.floor(max * 0.6);

  // 1) Drop trailing segments (brand tail, extra qualifiers) while they fit.
  for (const sep of TITLE_SEPARATORS) {
    if (!title.includes(sep)) continue;
    const parts = title.split(sep);
    let acc = parts[0].trim();
    if (acc.length > max) continue; // this separator can't help
    for (let i = 1; i < parts.length; i++) {
      const next = `${acc} ${sep.trim()} ${parts[i].trim()}`.replace(/\s+/g, " ").trim();
      if (next.length > max) break;
      acc = next;
    }
    if (trimEdge(acc).length >= min) return trimEdge(acc);
  }

  // 2) Latin fallback: cut on the last word boundary before the limit.
  const slice = title.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  if (lastSpace >= max * 0.6) return trimEdge(slice.slice(0, lastSpace));

  // 3) Thai has no word spaces — hard cut and tidy the edge.
  return trimEdge(slice);
}

/** Dangling function words that read badly at the end of a truncated title. */
const TRAILING_STOPWORDS =
  /\s+(?:and|or|the|a|an|at|of|in|on|for|to|with|from|by|&|\u0e41\u0e25\u0e30|\u0e2b\u0e23\u0e37\u0e2d|\u0e17\u0e35\u0e48|\u0e02\u0e2d\u0e07|\u0e43\u0e19|\u0e01\u0e31\u0e1a)$/i;

function trimEdge(input: string): string {
  let s = input.trim();
  // Drop an unbalanced open bracket group left behind by the cut.
  const open = s.lastIndexOf("(");
  if (open > -1 && !s.includes(")", open)) s = s.slice(0, open);
  s = s.replace(/[\s\u00b7|,\-\u2013\u2014]+$/g, "").trim();
  while (TRAILING_STOPWORDS.test(s)) s = s.replace(TRAILING_STOPWORDS, "");
  return s.replace(/[\s\u00b7|,\-\u2013\u2014]+$/g, "").trim();
}
